import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import { useUser } from '../components/UserContext';
import { useOrgMiembros } from '../hooks/useOrgMiembros';
import { useOrdenesTrabajo } from '../hooks/useOrdenesTrabajo';
import OtTablero from '../components/ot/OtTablero';
import OtDialog from '../components/ot/OtDialog';
import OtView from '../components/ot/OtView';
import OtCarga from '../components/ot/OtCarga';
import OtEstadisticas from '../components/ot/OtEstadisticas';
import OtBandeja from '../components/ot/OtBandeja';
import OtPasarFaseDialog from '../components/ot/OtPasarFaseDialog';
import OtRechazarDialog from '../components/ot/OtRechazarDialog';
import OtAprobarDialog from '../components/ot/OtAprobarDialog';
import OtAprobaciones from '../components/ot/OtAprobaciones';
import { AsignadosBubbles } from '../components/AsignadoBubble';
import { tipoBadgeClass } from '../components/ot/OtTarjeta';
import {
  OT_TIPOS, OT_RESULTADO_LABELS,
  folioTexto, isAbierta, esMia, normalizarAsignados, diasEnFase, diasAbierta, ordenarOts, faseDe, fasesDe,
  esCoordinador, otsPorAsignar, esAprobador, otsPorAprobar,
} from '../utils/ordenesTrabajo';
import { ClipboardDocumentCheckIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';

// Carga, Estadísticas and the bandeja are the coordinador's (Fran,
// 2026-10-19: "que la parte de estadísticas solo la vea esa persona").
const VISTAS = [
  { key: 'tablero', label: 'Tablero' },
  { key: 'lista', label: 'Lista' },
  { key: 'bandeja', label: 'Por asignar', coordinador: true },
  { key: 'aprobaciones', label: 'Aprobaciones', coordinador: true },
  { key: 'carga', label: 'Carga', coordinador: true },
  { key: 'estadisticas', label: 'Estadísticas', coordinador: true },
];

const field =
  'rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm outline-none bg-white ' +
  'focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia';

const formatFecha = (ms) => (ms ? new Date(Number(ms)).toLocaleDateString('es-MX', {
  day: 'numeric', month: 'short', year: 'numeric',
}) : '—');

/**
 * /app/ordenes — Órdenes de Trabajo. The work surface for the team that
 * operates the trámite: a kanban by fase, a list with history, who carries
 * what, and processing statistics. Org add-on (orgs.otEnabled); the route is
 * reachable only when it is on.
 */
export default function OrdenesTrabajo() {
  const { user, isOwner } = useUser();
  const { rows, abiertas, loading, otEnabled, config } = useOrdenesTrabajo();
  const coordinador = esCoordinador(config, user?.uid, isOwner);
  const aprobador = esAprobador(config, user?.uid, isOwner);
  const { miembros } = useOrgMiembros();
  const [searchParams, setSearchParams] = useSearchParams();

  const [vista, setVista] = useState('tablero');
  const [soloMias, setSoloMias] = useState(false);
  const [equipoId, setEquipoId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [tipo, setTipo] = useState('');
  // Each tipo has its own fases, so the tablero shows one flow at a time.
  const [tipoTablero, setTipoTablero] = useState(OT_TIPOS.FIANZA);
  const [busqueda, setBusqueda] = useState('');
  const [verCerradas, setVerCerradas] = useState(false);
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [pasar, setPasar] = useState(null); // { ot, faseId } while the relevo dialog is open
  const [rechazar, setRechazar] = useState(null); // ot
  const [aprobar, setAprobar] = useState(null); // ot

  const otAbiertaId = searchParams.get('ot');
  const otAbierta = useMemo(() => (rows || []).find((r) => r.id === otAbiertaId) || null, [rows, otAbiertaId]);
  const abrir = (ot) => setSearchParams(ot ? { ot: ot.id } : {}, { replace: true });
  const cerrarVista = () => setSearchParams({}, { replace: true });

  const filtrar = (lista) => {
    const q = busqueda.trim().toLowerCase();
    return lista.filter((ot) => {
      if (soloMias && !esMia(ot, user?.uid, config)) return false;
      if (tipo && ot.tipo !== tipo) return false;
      if (personaId && !normalizarAsignados(ot).includes(personaId)) return false;
      if (equipoId) {
        const enEquipo = (ot.equipos || []).includes(equipoId);
        const faseEquipo = faseDe(fasesDe(config, ot.tipo), ot.faseId)?.equipoId === equipoId;
        if (!enEquipo && !faseEquipo) return false;
      }
      if (q) {
        const texto = `${folioTexto(ot.folio)} ${ot.titulo} ${ot.clientName} ${ot.contractTitle} ${ot.contractNumber} ${ot.fianzaNumero}`.toLowerCase();
        if (!texto.includes(q)) return false;
      }
      return true;
    });
  };

  const abiertasFiltradas = useMemo(() => filtrar(abiertas),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [abiertas, soloMias, tipo, personaId, equipoId, busqueda, config, user?.uid]);
  const tableroRows = useMemo(
    () => abiertasFiltradas.filter((o) => (o.tipo || OT_TIPOS.FIANZA) === tipoTablero),
    [abiertasFiltradas, tipoTablero]
  );
  const conteoTablero = useMemo(() => {
    const c = {};
    abiertasFiltradas.forEach((o) => { const t = o.tipo || OT_TIPOS.FIANZA; c[t] = (c[t] || 0) + 1; });
    return c;
  }, [abiertasFiltradas]);
  const listaFiltrada = useMemo(() => ordenarOts(filtrar(verCerradas ? (rows || []) : abiertas)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, abiertas, verCerradas, soloMias, tipo, personaId, equipoId, busqueda, config, user?.uid]);

  const resumen = useMemo(() => {
    const now = Date.now();
    return {
      abiertas: abiertas.length,
      mias: abiertas.filter((o) => esMia(o, user?.uid, config)).length,
      sinAsignar: abiertas.filter((o) => normalizarAsignados(o).length === 0).length,
      vencidas: abiertas.filter((o) => o.fechaCompromiso && Number(o.fechaCompromiso) < now).length,
      porAsignar: coordinador ? otsPorAsignar(abiertas, config).length : 0,
      porAprobar: aprobador ? otsPorAprobar(abiertas, config).length : 0,
    };
  }, [abiertas, user?.uid, config, coordinador, aprobador]);
  // The bandeja is also the aprobador's; Carga and Estadísticas stay the coordinador's.
  const vistas = VISTAS.filter((v) => !v.coordinador || coordinador || (['bandeja', 'aprobaciones'].includes(v.key) && aprobador));

  if (!otEnabled) {
    return (
      <div className="p-6">
        <Heading level={1}>Órdenes de trabajo</Heading>
        <Text className="mt-4">Esta función no está activa en tu organización.</Text>
      </div>
    );
  }

  const hayFiltro = soloMias || equipoId || personaId || tipo || busqueda;
  const limpiar = () => { setSoloMias(false); setEquipoId(''); setPersonaId(''); setTipo(''); setBusqueda(''); };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Heading level={1}>Órdenes de trabajo</Heading>
          <Text className="mt-1 text-sm text-zinc-500">
            {resumen.abiertas} abiertas · {resumen.mias} mías · {resumen.sinAsignar} sin asignar
            {resumen.porAsignar > 0 && <span className="text-emerald-700"> · {resumen.porAsignar} por asignar</span>}
            {resumen.porAprobar > 0 && <span className="text-amber-700"> · {resumen.porAprobar} por aprobar</span>}
            {resumen.vencidas > 0 && <span className="text-red-600"> · {resumen.vencidas} con compromiso vencido</span>}
          </Text>
        </div>
        <Button color="surelia" onClick={() => setNuevaOpen(true)}>Nueva orden</Button>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap border-b border-azul-surelia/20">
        <div className="flex gap-1">
          {vistas.map((v) => (
            <button
              key={v.key}
              onClick={() => setVista(v.key)}
              className={clsx(
                'px-3 py-2 text-sm font-semibold border-b-2 transition-colors inline-flex items-center gap-1.5',
                vista === v.key
                  ? 'text-azul-surelia border-azul-surelia'
                  : 'text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40',
              )}
            >
              {v.label}
              {v.key === 'bandeja' && resumen.porAsignar + resumen.porAprobar > 0 && (
                <span className="rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums">{resumen.porAsignar + resumen.porAprobar}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {(vista === 'tablero' || vista === 'lista') && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setSoloMias((v) => !v)}
            className={clsx(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              soloMias ? 'border-azul-surelia bg-azul-surelia text-white' : 'border-zinc-300 text-zinc-600 hover:border-azul-surelia/50',
            )}
          >
            Mías
          </button>
          <select value={equipoId} onChange={(e) => setEquipoId(e.target.value)} className={field}>
            <option value="">Todos los equipos</option>
            {config.equipos.map((eq) => <option key={eq.id} value={eq.id}>{eq.nombre}</option>)}
          </select>
          <select value={personaId} onChange={(e) => setPersonaId(e.target.value)} className={field}>
            <option value="">Cualquier asignado</option>
            {miembros.map((m) => <option key={m.id} value={m.id}>{m.nombre || m.correo}</option>)}
          </select>
          {vista === 'tablero' ? (
            <div className="inline-flex rounded-lg border border-zinc-300 overflow-hidden text-xs font-medium">
              {config.flujos.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setTipoTablero(f.id)}
                  className={clsx('px-3 py-1.5 transition-colors', tipoTablero === f.id ? 'bg-azul-surelia text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50')}
                >
                  {f.nombre} <span className="tabular-nums opacity-80">{conteoTablero[f.id] || 0}</span>
                </button>
              ))}
            </div>
          ) : (
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={field}>
              <option value="">Todos los tipos</option>
              {config.flujos.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            </select>
          )}
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Folio, nombre, fiado, fianza"
            className={clsx(field, 'min-w-[14rem]')}
          />
          {vista === 'lista' && (
            <label className="inline-flex items-center gap-1.5 text-xs text-zinc-600 ml-1">
              <input type="checkbox" checked={verCerradas} onChange={(e) => setVerCerradas(e.target.checked)} className="rounded border-zinc-300 text-azul-surelia" />
              Ver cerradas
            </label>
          )}
          {hayFiltro && (
            <button onClick={limpiar} className="text-xs text-zinc-500 hover:text-azul-surelia ml-1">Limpiar</button>
          )}
        </div>
      )}

      {loading || rows === null ? (
        <Text className="text-sm text-zinc-500">Cargando…</Text>
      ) : vista === 'tablero' ? (
        abiertas.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-zinc-400 bg-white rounded-lg border border-zinc-200">
            <ClipboardDocumentCheckIcon className="w-10 h-10" />
            <Text className="text-sm text-zinc-500">Sin órdenes abiertas.</Text>
          </div>
        ) : (
          <OtTablero
            rows={tableroRows}
            fases={fasesDe(config, tipoTablero)}
            config={config}
            miembros={miembros}
            puedeMover={coordinador}
            onMove={(ot, faseId) => setPasar({ ot, faseId })}
            onOpen={abrir}
          />
        )
      ) : vista === 'lista' ? (
        listaFiltrada.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-zinc-400 bg-white rounded-lg border border-zinc-200">
            <ClipboardDocumentCheckIcon className="w-10 h-10" />
            <Text className="text-sm text-zinc-500">No hay órdenes con estos filtros.</Text>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
                  <th className="px-3 py-2 w-full">Orden</th>
                  <th className="px-3 py-2 whitespace-nowrap">Inicio</th>
                  <th className="px-3 py-2 whitespace-nowrap">Fase</th>
                  <th className="px-3 py-2">Asignados</th>
                  <th className="px-3 py-2 whitespace-nowrap">{verCerradas ? 'Cierre' : 'Compromiso'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {listaFiltrada.map((ot) => {
                  const cerrada = !isAbierta(ot);
                  return (
                    <tr key={ot.id} className={clsx('hover:bg-zinc-50 cursor-pointer', cerrada && 'text-zinc-500')} onClick={() => abrir(ot)}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold tabular-nums text-azul-surelia">{folioTexto(ot.folio)}</span>
                          <span className={clsx('rounded-full px-2 py-0.5 text-[10px] font-medium', tipoBadgeClass(ot.tipo))}>
                            {ot.tipoNombre || ot.tipo}
                          </span>
                          {Number(ot.mensajes) > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[11px] text-zinc-400">
                              <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5" />{ot.mensajes}
                            </span>
                          )}
                        </div>
                        <div className={clsx('font-medium', cerrada ? 'text-zinc-500' : 'text-zinc-800')}>{ot.titulo}</div>
                      </td>
                      <td className="px-3 py-2.5 text-zinc-600 whitespace-nowrap tabular-nums">
                        <div>{formatFecha(ot.createdAt)}</div>
                        <div className="text-xs text-zinc-400">{cerrada ? `${diasAbierta(ot)} d de trabajo` : `hace ${diasAbierta(ot)} d`}</div>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {cerrada ? (
                          <span className="text-zinc-600">Cerrada · {OT_RESULTADO_LABELS[ot.cierre?.resultado] || ''}</span>
                        ) : (
                          <>
                            <div className="text-zinc-700">{ot.faseNombre || '—'}</div>
                            <div className={clsx('text-xs tabular-nums', ot.faseEstado === 'vencida' ? 'text-red-600 font-medium' : ot.faseEstado === 'porVencer' ? 'text-amber-600' : 'text-zinc-400')}>
                              desde {formatFecha(ot.faseDesde)} · {diasEnFase(ot)} d{ot.faseObjetivo ? ` de ${ot.faseObjetivo}` : ''}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <AsignadosBubbles uids={normalizarAsignados(ot)} miembros={miembros} size="sm" />
                      </td>
                      <td className="px-3 py-2.5 text-zinc-600 whitespace-nowrap">
                        {cerrada ? formatFecha(ot.cierre?.at) : (
                          <span className={clsx(ot.fechaCompromiso && Number(ot.fechaCompromiso) < Date.now() ? 'text-red-600' : '')}>
                            {formatFecha(ot.fechaCompromiso)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : vista === 'bandeja' && (coordinador || aprobador) ? (
        <OtBandeja
          abiertas={abiertas}
          config={config}
          miembros={miembros}
          puedeAsignar={coordinador}
          puedeAprobar={aprobador}
          onPasar={(ot, faseId) => setPasar({ ot, faseId })}
          onAprobar={(ot) => setAprobar(ot)}
          onRechazar={(ot) => setRechazar(ot)}
          onOpen={abrir}
        />
      ) : vista === 'aprobaciones' && (coordinador || aprobador) ? (
        <OtAprobaciones rows={rows} miembros={miembros} onOpen={abrir} />
      ) : vista === 'carga' && coordinador ? (
        <OtCarga
          abiertas={abiertas}
          config={config}
          miembros={miembros}
          onPersona={(uid) => { setPersonaId(uid); setEquipoId(''); setVista('tablero'); }}
          onEquipo={(id) => { setEquipoId(id); setPersonaId(''); setVista('tablero'); }}
        />
      ) : vista === 'estadisticas' && coordinador ? (
        <OtEstadisticas rows={rows} config={config} onOpen={abrir} />
      ) : null}

      <OtDialog isOpen={nuevaOpen} onClose={() => setNuevaOpen(false)} onSaved={(ot) => abrir(ot)} />
      <OtPasarFaseDialog
        isOpen={Boolean(pasar)}
        onClose={() => setPasar(null)}
        ot={pasar ? (rows || []).find((r) => r.id === pasar.ot.id) || pasar.ot : null}
        faseDestinoId={pasar?.faseId || null}
      />
      <OtAprobarDialog
        isOpen={Boolean(aprobar)}
        onClose={() => setAprobar(null)}
        ot={aprobar ? (rows || []).find((r) => r.id === aprobar.id) || aprobar : null}
      />
      <OtRechazarDialog
        isOpen={Boolean(rechazar)}
        onClose={() => setRechazar(null)}
        ot={rechazar ? (rows || []).find((r) => r.id === rechazar.id) || rechazar : null}
      />
      <OtView ot={otAbierta} isOpen={Boolean(otAbierta)} onClose={cerrarVista} />
    </div>
  );
}
